/* ==========================================================================
   Fuel Pump Size Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-fpc]');
  if (!root) return;

  /* Brake specific fuel consumption, pounds of fuel per horsepower per hour.
     Naturally aspirated gasoline engines sit near 0.50. Boosted engines run
     richer to control detonation. Ethanol carries less energy per pound so
     E85 needs far more mass flow for the same power. */
  var BSFC = {
    na:      0.50,
    boosted: 0.62,
    e85na:   0.72,
    e85b:    0.85
  };

  /* Fuel density at typical temperatures. */
  var LB_PER_GAL = { gas: 6.073, e85: 6.59 };
  var L_PER_GAL = 3.785411784;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.hp) || input.hp <= 0) {
      return { ok: false, errors: ['Enter your target horsepower.'] };
    }
    var bsfc = BSFC[input.setup] || BSFC.na;
    var isE85 = input.setup === 'e85na' || input.setup === 'e85b';
    var density = isE85 ? LB_PER_GAL.e85 : LB_PER_GAL.gas;
    var margin = isFinite(input.margin) && input.margin >= 0 ? input.margin / 100 : 0.25;

    var lbHr = input.hp * bsfc;
    var galHr = lbHr / density;
    var lph = galHr * L_PER_GAL;
    var lphWithMargin = lph * (1 + margin);

    var figures = [
      { label: 'Pump flow needed', value: num(lphWithMargin, 0) + ' LPH',
        sub: num(lph, 0) + ' LPH of actual demand plus a ' + num(margin * 100, 0) + '% margin.' },
      { label: 'Fuel consumed at full power', value: num(galHr, 1) + ' gal/hr',
        sub: num(lbHr, 0) + ' lb/hr. That is a tank every ' + num(20 / galHr * 60, 0) + ' minutes from a 20 gallon tank.' }
    ];

    var status, warnings = [];
    if (isFinite(input.have) && input.have > 0) {
      var supported = ((input.have / (1 + margin)) / L_PER_GAL) * density / bsfc;
      figures.push({ label: 'Your pump supports', value: comma(Math.round(supported)) + ' hp',
        sub: 'At ' + num(input.have, 0) + ' LPH with the same margin applied.' });

      if (input.have < lph) {
        status = { key: 'critical', label: 'Not enough pump',
                   note: 'Below even the bare demand of ' + num(lph, 0) + ' LPH. The engine will run lean at full load.' };
      } else if (input.have < lphWithMargin) {
        status = { key: 'soon', label: 'No margin',
                   note: 'Covers demand but not the ' + num(margin * 100, 0) + '% margin. Pumps fade with age and heat.' };
      } else {
        status = { key: 'good', label: num(input.have, 0) + ' LPH',
                   note: 'Supports about ' + comma(Math.round(supported)) + ' hp with margin intact.' };
      }
    } else {
      status = { key: 'good', label: num(lphWithMargin, 0) + ' LPH',
                 note: 'For ' + comma(Math.round(input.hp)) + ' hp at ' + num(bsfc, 2) + ' BSFC with a ' + num(margin * 100, 0) + '% margin.' };
    }

    if (isE85) {
      warnings.push('E85 needs far more volume than gasoline and is harder on pump internals. Confirm the pump is rated for ethanol, and expect a shorter service life.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status, figures: figures,
      rows: [
        { head: 'Demand' },
        { label: 'Target power', value: comma(Math.round(input.hp)) + ' hp' },
        { label: 'BSFC', value: num(bsfc, 2) },
        { label: 'Mass flow', value: num(lbHr, 0) + ' lb/hr' },
        { label: 'Volume flow', value: num(lph, 0) + ' LPH  (' + num(galHr, 1) + ' gal/hr)' },
        { head: 'With margin' },
        { label: 'Margin', value: num(margin * 100, 0) + '%' },
        { label: 'Pump needed', value: num(lphWithMargin, 0) + ' LPH' },
        { head: 'Remember' },
        { label: 'Ratings are at', value: '13.5V and low pressure' },
        { label: 'At 60 psi expect', value: 'roughly 25% less' },
        { label: 'At 12.0V expect', value: 'roughly 15% less' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "hp",
          "key": "p",
          "type": "number"
      },
      {
          "name": "setup",
          "key": "s",
          "type": "select"
      },
      {
          "name": "margin",
          "key": "m",
          "type": "number"
      },
      {
          "name": "have",
          "key": "h",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-fpc__figure">'
        + '<div class="pc-fpc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-fpc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-fpc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-fpc__row pc-fpc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-fpc__row">'
        + '<span class="pc-fpc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-fpc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_FPC = { compute: compute };
})();
